import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Check, X, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/schedule`;
const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
function iso(d) { return d.toISOString().slice(0, 10); }

export default function PublicScheduleManage() {
  const { token } = useParams();
  const [b, setB] = useState(null);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("");
  const [date, setDate] = useState(new Date());
  const [slots, setSlots] = useState(null);

  const load = () => axios.get(`${API}/manage/${token}`).then((r) => setB(r.data)).catch(() => setErr("Booking not found."));
  useEffect(() => { load(); }, [token]);
  useEffect(() => {
    if (mode !== "reschedule" || !b?.slug) return;
    setSlots(null);
    axios.get(`${API}/${b.slug}/slots`, { params: { appointment_type_id: b.appointment_type_id, date: iso(date), tz: TZ } })
      .then((r) => setSlots(r.data.slots)).catch(() => setSlots([]));
  }, [mode, date, b]);

  const cancel = async () => { try { await axios.post(`${API}/manage/${token}/cancel`); toast.success("Cancelled"); load(); setMode(""); } catch { toast.error("Could not cancel."); } };
  const reschedule = async (start) => { try { await axios.post(`${API}/manage/${token}/reschedule`, { start }); toast.success("Rescheduled"); load(); setMode(""); } catch (e) { toast.error(e?.response?.data?.detail || "Could not reschedule."); } };

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4"><div className="max-w-lg mx-auto bg-white rounded-2xl shadow-sm border border-slate-100 p-6 sm:p-8">
      {err ? <p className="text-center text-slate-500 py-10">{err}</p> : !b ? <p className="text-center text-slate-400 py-10">Loading…</p> : (
        <div data-testid="manage-booking">
          <div className="flex items-center gap-2 mb-4"><CalendarClock className="w-5 h-5 text-brand" /><h1 className="text-lg font-bold text-slate-800">Your Appointment</h1></div>
          <div className="rounded-xl border border-slate-200 p-4 text-sm space-y-1">
            <p><span className="text-slate-400">Type:</span> <b>{b.appointment_type}</b></p>
            <p><span className="text-slate-400">With:</span> {b.recruiter}</p>
            <p><span className="text-slate-400">When:</span> {new Date(b.start).toLocaleString(undefined, { dateStyle: "full", timeStyle: "short" })} · {TZ}</p>
            <p><span className="text-slate-400">Status:</span> <b className={b.status === "cancelled" ? "text-rose-600" : "text-emerald-600"}>{b.status}</b></p>
          </div>
          {b.status !== "cancelled" && !mode && (
            <div className="flex gap-2 mt-4">
              <Button variant="outline" data-testid="mng-reschedule" onClick={() => setMode("reschedule")} className="flex-1">Reschedule</Button>
              <Button variant="outline" data-testid="mng-cancel" onClick={() => setMode("cancel")} className="flex-1 text-rose-600 border-rose-200 hover:bg-rose-50">Cancel</Button>
            </div>
          )}
          {mode === "cancel" && (
            <div className="mt-4 text-center" data-testid="cancel-confirm">
              <p className="text-sm text-slate-600 mb-3">Cancel this appointment?</p>
              <div className="flex gap-2"><Button variant="outline" onClick={() => setMode("")} className="flex-1"><X className="w-4 h-4 mr-1" /> Keep it</Button>
                <Button onClick={cancel} data-testid="cancel-yes" className="flex-1 bg-rose-600 hover:bg-rose-700">Yes, cancel</Button></div>
            </div>
          )}
          {mode === "reschedule" && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <button onClick={() => setDate(new Date(date.getTime() - 864e5))} className="px-2 py-1 rounded hover:bg-slate-100 text-sm">‹</button>
                <p className="font-medium text-slate-700 text-sm">{date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</p>
                <button onClick={() => setDate(new Date(date.getTime() + 864e5))} className="px-2 py-1 rounded hover:bg-slate-100 text-sm">›</button>
              </div>
              {slots === null ? <p className="text-sm text-slate-400 text-center py-4">Loading…</p>
                : slots.length === 0 ? <p className="text-sm text-slate-400 text-center py-4">No times this day.</p>
                : <div className="grid grid-cols-3 gap-2">{slots.map((s) => <button key={s.start} data-testid={`re-slot-${s.start}`} onClick={() => reschedule(s.start)} className="rounded-lg border border-slate-200 hover:border-brand py-2 text-sm">{s.label}</button>)}</div>}
              <button onClick={() => setMode("")} className="text-xs text-slate-400 mt-3">Back</button>
            </div>
          )}
        </div>
      )}
    </div></div>
  );
}
